import {
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import { AddIcon, DeleteIcon } from '../../icons'
import { strings } from '../../strings'
import {
  SubmitState,
  useDeletePermission,
  useMountpointGroupsList,
  useNewPermission,
  usePermissionsList,
  useUserGroupsList,
} from '../../api'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Autocomplete } from '@material-ui/lab'
import { useState } from 'react'
import { ConfirmIconButton } from '../../ConfirmIconButton'
import SourceTable from './SourceTable'

export function Permissions() {
  const classes = useStyles()
  const newPermission = useNewPermission()
  const [submitState, setSubmitState] = useState(SubmitState.None)
  const {
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
    control,
  } = useForm({
    mode: 'onChange',
  })

  const onSubmit: SubmitHandler<any> = (formData: any) => {
    setSubmitState(SubmitState.Submitting)
    newPermission(formData)
      .then(() => {
        setSubmitState(SubmitState.Success)
        reset({ userGroupId: null, mountpointGroupId: null })
      })
      .catch((err) => {
        console.log(err.message)
        setSubmitState(SubmitState.Error)
      })
  }

  return (
    <>
      <SourceTable />
      <Paper className={classes.result}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>{strings.userGroups}</th>
                <th>{strings.mountpointGroups}</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <UserGroupSelector control={control} />
                </td>
                <td>
                  <MountpointGroupSelector control={control} />
                </td>
                <td>
                  {submitState === SubmitState.Submitting && <CircularProgress size={20} />}
                  {submitState !== SubmitState.Submitting && (
                    <IconButton
                      type="submit"
                      className={classes.lastbtn}
                      disabled={!isDirty || !isValid}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </td>
              </tr>
              <DataTable classes={classes} />
            </tbody>
          </table>
        </form>
      </Paper>
    </>
  )
}

function UserGroupSelector({ control }: any) {
  const { userGroupsList, loading, error } = useUserGroupsList()

  if (loading) return <CircularProgress size={16} />
  if (error) return <Typography color="error">{error.message}</Typography>
  if (!userGroupsList) return <Typography color="error">{strings.notExists}</Typography>

  return (
    <Controller
      name="userGroupId"
      control={control}
      rules={{ required: true }}
      defaultValue={null}
      render={({ value, onChange }) => (
        <Autocomplete
          freeSolo
          clearOnBlur
          options={userGroupsList.sort((a, b) => a.id - b.id)}
          getOptionLabel={(x) => x.name}
          value={value && userGroupsList.find((x) => x.id === value)}
          onChange={(e, x: any) => {
            onChange(x ? x.id : null)
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              autoComplete="off"
              variant="outlined"
              label={strings.userGroups}
            />
          )}
        />
      )}
    />
  )
}

function MountpointGroupSelector({ control }: any) {
  const { mountpointGroupsList, loading, error } = useMountpointGroupsList()

  if (loading) return <CircularProgress size={16} />
  if (error) return <Typography color="error">{error.message}</Typography>
  if (!mountpointGroupsList) return <Typography color="error">{strings.notExists}</Typography>

  return (
    <Controller
      name="mountpointGroupId"
      control={control}
      rules={{ required: true }}
      defaultValue={null}
      render={({ value, onChange }) => (
        <Autocomplete
          freeSolo
          clearOnBlur
          options={mountpointGroupsList.sort((a, b) => a.id - b.id)}
          getOptionLabel={(x) => x.name}
          value={value && mountpointGroupsList.find((x) => x.id === value)}
          onChange={(e, x: any) => {
            onChange(x ? x.id : null)
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="dense"
              autoComplete="off"
              variant="outlined"
              label={strings.mountpointGroups}
            />
          )}
        />
      )}
    />
  )
}

function DataTable({ classes }: any) {
  const { permissionsList, loading, error } = usePermissionsList()
  const deletePermission = useDeletePermission()

  const handleDeleteClick = (userGroupId: number, mountpointGroupId: number) => {
    deletePermission({ userGroupId, mountpointGroupId }).catch((err) => console.log(err.message))
  }

  if (loading)
    return (
      <Row>
        <CircularProgress size={20} />
      </Row>
    )

  if (error)
    return (
      <Row>
        <Typography color="error">{error.message}</Typography>
      </Row>
    )

  if (!permissionsList)
    return (
      <Row>
        <Typography color="error">{strings.notExists}</Typography>
      </Row>
    )

  return (
    <>
      {permissionsList
        .sort((a, b) => a.userGroupId - b.userGroupId || a.mountpointGroupId - b.mountpointGroupId)
        .map((x, ix) => (
          <tr key={ix}>
            <td>{x.userGroupName}</td>
            <td>{x.mountpointGroupName}</td>
            <td>
              <ConfirmIconButton
                className={classes.lastbtn}
                onClick={() => handleDeleteClick(x.userGroupId, x.mountpointGroupId)}
              >
                <DeleteIcon />
              </ConfirmIconButton>
            </td>
          </tr>
        ))}
    </>
  )
}

function Row({ children }: any) {
  return (
    <tr>
      <td colSpan={3} style={{ textAlign: 'center' }}>
        {children}
      </td>
    </tr>
  )
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexFlow: 'column',
    height: '100%',
  },
  result: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    backgroundColor: '#fff',
    padding: theme.spacing(2),
  },
  lastbtn: {
    padding: theme.spacing(1),
    size: 'small',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& td': {
      paddingTop: theme.spacing(1),
    },
    '& th': {
      borderBottom: '1px solid lightgray',
      fontWeight: 'normal',
      textAlign: 'left',
      paddingBottom: theme.spacing(0.5),
    },
    '& th:last-child': {
      width: theme.spacing(3.5),
    },
  },
}))
